import { useRef, useState } from "react";
import { apiPcf, type PedidoComplementar, type ItemPedido, type AvisoPcf } from "../lib/api";
import type { PcfFormModo } from "./PcfForm";
import { useTheme } from "../hooks/useTheme";
import { Logo } from "../components/Logo";
import { logout, type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { decimalParaInput, mascaraDecimal, parseDecimalBR } from "../lib/decimal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";

// atcadpcf / nova-complementar "Nova PCF Complementar": nº pedido → cliente + item
// (TELA-ITENS-PEDIDO) → aviso de PCFs existentes (TELA-AVISO) → abre a TELA-PCF
// como COMPLEMENTAR. Fiel a T2-NUMPED-WS-LostFocus / PESQUISAR-PRODUTO / MOVER.

interface Props {
  usuario: Usuario;
  onVoltar: () => void;
  onAbrirPcf: (modo: PcfFormModo) => void;
}

const nf2 = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2 = (n: number) => nf2.format(n || 0);
// Máscaras do form (16/10/2026): Comprimento ZZZ.ZZZ,99 (2 casas) e Quantidade ZZZZZZZZ (inteiro).
const parseDec = (s: string) => parseDecimalBR(s); // aceita milhar ("6.000,00") do valor preenchido

export function NovaComplementar({ usuario, onVoltar, onAbrirPcf }: Props) {
  const { theme, toggle } = useTheme();
  const [numped, setNumped] = useState("");
  const [pedido, setPedido] = useState<PedidoComplementar | null>(null);
  const [item, setItem] = useState<ItemPedido | null>(null);
  const [avisos, setAvisos] = useState<AvisoPcf[]>([]);
  const [comprimento, setComprimento] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [datent, setDatent] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const ordIt = useOrdenacao(pedido?.itens, {
    seq: (i) => i.sequen, cod: (i) => i.codpro, pro: (i) => i.nompro, qtd: (i) => i.qtdped,
    comp: (i) => i.comprimento, liq: (i) => i.valliq, tot: (i) => i.totite,
  });
  const ordAv = useOrdenacao(avisos, { pcf: (a) => a.numpcf, cli: (a) => a.nomcli, qtd: (a) => a.qtdpec, ent: (a) => a.datent });
  const formRef = useRef<HTMLDivElement>(null);

  function enterTab(e: React.KeyboardEvent) {
    if (e.key !== "Enter") return;
    const alvo = e.target as HTMLElement;
    if (alvo.tagName === "TEXTAREA") return;
    e.preventDefault();
    const foc = Array.from(
      formRef.current?.querySelectorAll<HTMLElement>("input,select,button") ?? []
    ).filter((x) => !(x as HTMLInputElement).disabled && x.tabIndex !== -1 && x.offsetParent !== null);
    const i = foc.indexOf(alvo);
    if (i >= 0 && i < foc.length - 1) foc[i + 1]?.focus();
  }

  async function buscarPedido() {
    const np = Number(numped) || 0;
    setAviso(null);
    setErro(null);
    setItem(null);
    setAvisos([]);
    setPedido(null);
    if (np <= 0) return;
    try {
      const p = await apiPcf.pedidoComplementar(np);
      if (p.erro) {
        setAviso(p.erro);
        return;
      }
      setPedido(p);
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  async function escolherItem(it: ItemPedido) {
    setItem(it);
    setComprimento(it.comprimento ? decimalParaInput(it.comprimento, 2) : "");
    setQuantidade(it.qtdped ? String(it.qtdped).replace(".", ",") : "");
    try {
      setAvisos(await apiPcf.avisos(Number(numped) || 0, it.codpro));
    } catch {
      setAvisos([]);
    }
  }

  // botao-limpar
  function limpar() {
    setNumped("");
    setPedido(null);
    setItem(null);
    setAvisos([]);
    setComprimento("");
    setQuantidade("");
    setDatent("");
    setAviso(null);
    setErro(null);
  }

  function gravar() {
    setAviso(null);
    if (!pedido || !item) return setAviso("Selecione o pedido e o item.");
    const qtd = parseDec(quantidade);
    const comp = parseDec(comprimento);
    if (item.codpro === 0) return setAviso("Produto de produção não informada");
    if (qtd === 0) return setAviso("Quantidade de produto não informada!");
    if (comp === 0) return setAviso("Comprimento não informado!");
    if (!datent) return setAviso("Data não informada!");
    onAbrirPcf({
      kind: "nova",
      tipo: "COMPLEMENTAR",
      numped: Number(numped) || 0,
      sequen: item.sequen,
      codcli: pedido.codcli,
      codpro: item.codpro,
      qtd,
      comprimento: comp,
      datent,
    });
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="brand">
          <Logo />
          <h1>
            PCP - Ordem Complementar
            <small>Plano de Controle de Fabricação (PCF)</small>
          </h1>
        </div>
        <span className="spacer" />
        <div className="user-badge" title={`Usuário: ${usuario.nomusu}`}>
          <span className="user-avatar">{usuario.apelid.charAt(0).toUpperCase()}</span>
          <span className="user-nome">{usuario.nomusu.split(" ")[0]}</span>
        </div>
        <button className="btn-secundario" onClick={onVoltar}>
          ← PCP
        </button>
        <button className="btn-secundario" onClick={toggle} title={theme === "light" ? "Modo escuro" : "Modo claro"}>{theme === "light" ? "Modo escuro" : "Modo claro"}</button>
        <button className="btn-secundario" onClick={logout} title="Sair">Sair</button>
      </header>

      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <MsgModal texto={aviso} tipo="aviso" onClose={() => setAviso(null)} />

      <div className="pcf-form" ref={formRef}>
        <section className="pcf-sec">
          <h2>Pedido</h2>
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>Pedido</span>
              <input
                className="num"
                inputMode="numeric"
                value={numped}
                onChange={(e) => setNumped(e.target.value.replace(/\D/g, ""))}
                onBlur={() => void buscarPedido()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void buscarPedido();
                  }
                }}
              />
            </label>
            <button className="btn-secundario" onClick={() => void buscarPedido()}>
              🔍 Buscar
            </button>
            {pedido && (
              <label className="pcf-campo pcf-lg">
                <span>Cliente</span>
                <input value={`${pedido.codcli} — ${pedido.nomcli}`} readOnly />
              </label>
            )}
          </div>
        </section>

        {pedido && (
          <section className="pcf-sec">
            <h2>Itens do pedido — selecione um</h2>
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ordIt} col="seq" className="num">Seq</ThOrdena>
                    <ThOrdena ord={ordIt} col="cod" className="num">Código</ThOrdena>
                    <ThOrdena ord={ordIt} col="pro">Produto</ThOrdena>
                    <ThOrdena ord={ordIt} col="qtd" className="num">Qtd</ThOrdena>
                    <ThOrdena ord={ordIt} col="comp" className="num">Comprimento</ThOrdena>
                    <ThOrdena ord={ordIt} col="liq" className="num">Vlr. Líquido</ThOrdena>
                    <ThOrdena ord={ordIt} col="tot" className="num">Total</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {pedido.itens.length === 0 && (
                    <tr>
                      <td colSpan={7} className="plan-vazio">
                        Pedido sem itens.
                      </td>
                    </tr>
                  )}
                  {ordIt.linhas.map((it) => (
                    <tr
                      key={it.sequen}
                      className={"row-clickable" + (item?.sequen === it.sequen ? " plan-sel" : "")}
                      onClick={() => void escolherItem(it)}
                    >
                      <td className="num">{it.sequen}</td>
                      <td className="num">{it.codpro}</td>
                      <td>{it.nompro}</td>
                      <td className="num">{fmt2(it.qtdped)}</td>
                      <td className="num">{fmt2(it.comprimento)}</td>
                      <td className="num">{fmt2(it.valliq)}</td>
                      <td className="num">{fmt2(it.totite)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {item && avisos.length > 0 && (
          <section className="pcf-sec">
            <h2 className="pcf-aviso-tit">⚠ PCFs já emitidas para este produto neste pedido</h2>
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ordAv} col="pcf" className="num">Nº PCF</ThOrdena>
                    <ThOrdena ord={ordAv} col="cli">Cliente</ThOrdena>
                    <ThOrdena ord={ordAv} col="qtd" className="num">Qtd</ThOrdena>
                    <ThOrdena ord={ordAv} col="ent">Entrega</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {ordAv.linhas.map((a) => (
                    <tr key={a.numpcf}>
                      <td className="num">{a.numpcf}</td>
                      <td>{a.nomcli}</td>
                      <td className="num">{fmt2(a.qtdpec)}</td>
                      <td>{a.datent ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {item && (
          <section className="pcf-sec">
            <h2>Dados da Complementar</h2>
            <div className="pcf-grid">
              <label className="pcf-campo pcf-lg">
                <span>Produto</span>
                <input value={`${item.codpro} — ${item.nompro}`} readOnly />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Comprimento</span>
                <input
                  className="num"
                  inputMode="decimal"
                  value={comprimento}
                  onChange={(e) => setComprimento(mascaraDecimal(e.target.value, 2))}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Quantidade</span>
                <input
                  className="num"
                  inputMode="numeric"
                  value={quantidade}
                  onChange={(e) => setQuantidade(e.target.value.replace(/\D/g, "").slice(0, 8))}
                  onKeyDown={enterTab}
                />
              </label>
              <label className="pcf-campo pcf-sm">
                <span>Previsão de Entrega</span>
                <input type="date" value={datent} onChange={(e) => setDatent(e.target.value)} onKeyDown={enterTab} />
              </label>
            </div>
          </section>
        )}

        <div className="pcf-acoes">
          <button className="btn-acao" disabled={!item} onClick={gravar}>
            Gravar
          </button>
          <button className="btn-secundario" onClick={limpar}>
            Limpar
          </button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
